/**
 * Schwartz circumplex vocabulary — the validated circular order of the ten basic
 * human values, their German sector labels, and the higher-order region each sits
 * in. ONE source of truth (no mirrors, §9): the api-v2 prognosis composer
 * (`value-model.ts`) AND the app-elements circumplex wheel both import from here, so
 * the wheel can never drift from the order/labels the profile is computed against.
 *
 * The order is Schwartz's own published circle projected to a line
 * (Self-Direction → Stimulation → Hedonism → Achievement → Power → Security →
 * Conformity → Tradition → Benevolence → Universalism), wrapping back to
 * Self-Direction. Adjacent sectors are motivationally compatible; opposite sectors
 * conflict — the reason the wheel renders them in THIS order and no other.
 *
 * German labels are the operator's ratified sector vocabulary (interview v3.2). The
 * higher-order region labels reuse `SchwartzHigherOrder` — the four regions the wheel
 * arcs over. `SchwartzBasicValue` / `SchwartzHigherOrder` are the frozen catalog
 * tokens (`value-card-catalog.ts`); this module only orders and labels them.
 */

import type { BigFiveTrait, SchwartzBasicValue, SchwartzHigherOrder } from "./value-card-catalog"

/**
 * The ten basic Schwartz values in the validated circumplex order (clockwise from
 * Self-Direction at the top). The wheel lays sectors out in exactly this order; the
 * prognosis emits one sector per entry in this order.
 */
export const SCHWARTZ_CIRCUMPLEX_ORDER: readonly SchwartzBasicValue[] = [
  "self_direction",
  "stimulation",
  "hedonism",
  "achievement",
  "power",
  "security",
  "conformity",
  "tradition",
  "benevolence",
  "universalism",
]

/**
 * German sector labels — the words a person reads off their own wheel.
 *
 * PROPOSAL, pending the operator's ruling on the actual German (values rework §5). The
 * previous set was the taxonomy's vocabulary rather than the person's: »Selbstausrichtung«,
 * »Stimulation«, »Hedonismus«, »Anpassung«, »Universalismus« are translated academic terms,
 * and recognition is the entire job of this surface — a real dogfood user's »Ich sehe mich
 * da nicht so richtig drin wieder« was about a picture labelled in those words.
 *
 * Each label is chosen against the CARDS that actually load its sector, not against the
 * theory's definition, because the cards are what the person just spent twenty minutes
 * with. »Halt & Sicherheit« carries Zugehörigkeit, Familie and Ruhe as well as Sicherheit;
 * »Füreinander« carries Treue, Empathie, Fürsorge and Verantwortung; »Eigener Weg« carries
 * Freiheit, Neugier, Authentizität and Selbstverwirklichung.
 *
 * »Tradition« stays as it is: it is already plain German, and the relabel exists to remove
 * jargon, not to churn words that work. Every label is at most seventeen characters — the
 * length the wheel already renders today (»Selbstausrichtung«).
 *
 * The KEYS never change. They are the audit trail, the bench's comparison axis and the
 * model's emission vocabulary; only what a human reads is in question here.
 */
export const SCHWARTZ_BASIC_LABEL_DE: Record<SchwartzBasicValue, string> = {
  self_direction: "Eigener Weg",
  stimulation: "Neues wagen",
  hedonism: "Genießen",
  achievement: "Etwas erreichen",
  power: "Einfluss haben",
  security: "Halt & Sicherheit",
  conformity: "Verlässlich sein",
  tradition: "Tradition",
  benevolence: "Füreinander",
  universalism: "Mensch und Natur",
}

/**
 * The taxonomy labels this vocabulary replaced. They stay resolvable FOREVER: a model may
 * still emit »Wohlwollen«, and a profile prognosed before the relabel is replayed by readers
 * that resolve labels back to keys. Dropping an alias would silently mis-attribute a sector
 * rather than fail — the one failure mode this whole rework exists to remove.
 */
export const SCHWARTZ_BASIC_LABEL_ALIASES_DE: Readonly<Record<string, SchwartzBasicValue>> = {
  Selbstausrichtung: "self_direction",
  Stimulation: "stimulation",
  Hedonismus: "hedonism",
  Erfolg: "achievement",
  Macht: "power",
  Sicherheit: "security",
  Anpassung: "conformity",
  Wohlwollen: "benevolence",
  Universalismus: "universalism",
}

/**
 * The higher-order region each basic value sits in — the four arcs the wheel groups
 * sectors under (and the prognosis attributes to). Matches the catalog's own
 * higher-order assignment for the canonical value of each sector; Hedonism bridges
 * Openness and Self-Enhancement in Schwartz's theory — we file it under Openness (the
 * catalog's own placement for »Freude«), noting the bridge in the wheel legend, not
 * the data.
 */
export const SCHWARTZ_REGION_OF_BASIC: Record<SchwartzBasicValue, SchwartzHigherOrder> = {
  self_direction: "openness_to_change",
  stimulation: "openness_to_change",
  hedonism: "openness_to_change",
  achievement: "self_enhancement",
  power: "self_enhancement",
  security: "conservation",
  conformity: "conservation",
  tradition: "conservation",
  benevolence: "self_transcendence",
  universalism: "self_transcendence",
}

/** The four higher-order regions, in the wheel's arc order (matches the sector order). */
export const SCHWARTZ_REGION_LABEL_DE: Record<SchwartzHigherOrder, string> = {
  openness_to_change: "Offenheit für Neues",
  self_enhancement: "Vorankommen",
  conservation: "Bewahren",
  self_transcendence: "Über sich hinaus",
}

/** True when a token is one of the ten canonical basic Schwartz values. */
export function isSchwartzBasicValue(token: string): token is SchwartzBasicValue {
  return (SCHWARTZ_CIRCUMPLEX_ORDER as readonly string[]).includes(token)
}

// ─── The canonical MODEL-DIMENSION vocabulary (the plan-block `dimension` set) ──
//
// A plan block's `dimension` may only ever be one of these keys. The list lives HERE, upstream of
// both the emission schema (`drafts.ts` — the enum the model is CONSTRAINED to) and the tolerant
// normalizer (`api-v2/src/workshop/value-model.ts` — the net behind a non-strict provider), so the
// schema, the prompt's interpolated list and the error message that names the allowed set are all
// the same table. Authored as exhaustive `Record`s rather than bare arrays: a new member of
// `SchwartzHigherOrder` / `BigFiveTrait` is then a COMPILE error here instead of a silent gap.

const SCHWARTZ_HIGHER_ORDER_MEMBERS = {
  conservation: true,
  self_transcendence: true,
  openness_to_change: true,
  self_enhancement: true,
} satisfies Record<SchwartzHigherOrder, true>

const BIG_FIVE_TRAIT_MEMBERS = {
  openness: true,
  conscientiousness: true,
  extraversion: true,
  agreeableness: true,
  emotional_stability: true,
} satisfies Record<BigFiveTrait, true>

/** The four Schwartz higher-order dimensions — the stored top level (§5a), in reporting order. */
export const SCHWARTZ_HIGHER_ORDER = Object.keys(SCHWARTZ_HIGHER_ORDER_MEMBERS) as readonly SchwartzHigherOrder[]

/** The five Big-Five traits — the working-style axis (§5a / Q4). */
export const BIG_FIVE_TRAITS = Object.keys(BIG_FIVE_TRAIT_MEMBERS) as readonly BigFiveTrait[]

/**
 * Every canonical machine key a plan block's `dimension` may carry, both frameworks. The ONE list
 * the emission enum, the prompt's interpolated legal set, and the normalizer's error message are
 * all built from.
 */
export const PLAN_DIMENSION_KEYS: readonly (SchwartzHigherOrder | BigFiveTrait)[] = [
  ...SCHWARTZ_HIGHER_ORDER,
  ...BIG_FIVE_TRAITS,
]
